<template>
  <v-form ref="formStepBreakdown" v-model="formStepBreakdownValid" @submit.prevent="submit">
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-xl mt-4" :border="false" :elevation="0">
          <v-row>
            <v-col cols="12">
              <h1>{{ $t('FILE_STEP_BREAKDOWN.TITLE') }}</h1>
            </v-col>
            <v-col cols="12">
              <p require-field="true">{{ $t('FILE_STEP_BREAKDOWN.DESCRIPTION') }}</p>
              <v-textarea
                v-model.trim="description"
                :placeholder="$t('FILE_STEP_BREAKDOWN.PLACEHOLDER.DESCRIPTION')"
                :rules="requiredRules"
                auto-grow
                rows="3"
                :maxlength="150"
                :counter="150"
              />
            </v-col>
            <v-col cols="12">
              <p>{{ $t('FILE_STEP_BREAKDOWN.PRODUCT_BREAKDOWN') }}</p>
              <v-select
                id="select-product-breakdown"
                v-model="selectedProductBreakdown"
                :items="productBreakdownList"
                :no-data-text="$t('COMMON.NO_DATA')"
                :placeholder="$t('FILE_STEP_BREAKDOWN.PLACEHOLDER.PRODUCT_BREAKDOWN')"
                item-title="description"
                item-value="technical_id"
                @update:modelValue="openDialog()"
                @click:clear="resetSelectedProductBreakdown()"
              >
                <template v-slot:selection="{ item }">
                  <v-chip size="small">
                    <span>{{ item.title }}</span>
                  </v-chip>
                </template>
                <template v-slot:item="{ item, props }">
                  <v-list-item :title="item.title" :value="item.value" v-bind="props">
                    <v-tooltip
                      v-if="shouldShowTooltip(item, 'select-product-breakdown')"
                      activator="parent"
                      location="bottom left"
                    >
                      {{ item.title }}
                    </v-tooltip>
                  </v-list-item>
                </template>
              </v-select>

              <div class="d-flex justify-end">
                <p
                  @click="resetSelectedProductBreakdown() && getProductList()"
                  class="show-sub-product"
                >
                  {{ $t('FILE_STEP_BREAKDOWN.BUTTON.NOT_FOUND_BREAKDOWN') }}
                </p>
              </div>
            </v-col>

            <v-col cols="12" v-if="productList.length > 0">
              <div class="d-flex flex-column flex-md-row justify-space-between">
                <p class="mb-4 mt-8">{{ $t('FILE_STEP_BREAKDOWN.LABEL.AFFECTED_ELEMENT') }}</p>
                <div class="not-found-element mb-4 mt-8" @click="productList.length && selectAll()">
                  {{ $t('FILE_STEP_BREAKDOWN.LABEL.NOT_FOUND_ELEMENT') }}
                </div>
              </div>

              <div class="horizontal-scroll">
                <div
                  v-for="product in productListWithoutPallet"
                  :key="product.technical_id"
                  class="product-wrapper mr-4 d-inline-block align-top"
                  :class="{ 'not-reparable': product.reparable === 'inactive' }"
                  @mouseover="hoveredProductId = product.technical_id"
                  @mouseout="hoveredProductId = null"
                >
                  <v-menu
                    open-on-hover
                    transition="none"
                    open-delay="0"
                    location="end"
                    :close-on-content-click="false"
                    :offset="[-120, -120]"
                  >
                    <template v-slot:activator="{ props }">
                      <div class="product" v-bind="props">
                        <v-radio
                          v-model="selectedProducts"
                          :disabled="product.reparable === 'inactive'"
                          :value="product.technical_id"
                          class="custom-radio d-flex align-center justify-center"
                          color="#559a1b"
                          multiple
                          @click.prevent="checkProduct(product)"
                        />
                        <v-img class="product-picture" :src="product.picture" />
                        <p>{{ $t('COMMON.PRODUCT_TYPE.' + product.product_type) }}</p>
                      </div>
                    </template>

                    <div
                      v-show="
                        product.reparable === 'inactive' &&
                        hoveredProductId === product.technical_id
                      "
                      class="dialog"
                    >
                      <div class="d-flex flew-row align-center justify-start">
                        <img alt="info-icon" class="mr-4" src="@/assets/icons/file/info.svg" />
                        <div>
                          <span class="font-weight-light">
                            {{
                              $t('FILE_STEP_BREAKDOWN.DIALOG.TITLE') +
                              ' ' +
                              $t('COMMON.PRODUCT_TYPE.' + product.product_type)
                            }}
                          </span>
                          <p class="font-weight-bold mb-0">
                            {{ product.comment }}
                          </p>
                        </div>
                      </div>
                      <p class="text-wrap mt-2 mb-0">
                        {{ $t('FILE_STEP_BREAKDOWN.DIALOG.CONTENT') }}
                      </p>
                    </div>
                  </v-menu>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col cols="12" class="d-flex justify-start">
        <v-btn
          :disabled="!formStepBreakdownValid || loading || !selectedProducts.length"
          :loading="loading"
          class="pl-15 pr-15"
          :class="
            !formStepBreakdownValid || loading || !selectedProducts.length
              ? 'boom-button-transparent'
              : 'boom-button'
          "
          type="submit"
          variant="outlined"
        >
          {{ $t('COMMON.BUTTON.CONTINUE') }}
          <img
            alt="arrow_small-icon"
            :class="
              !formStepBreakdownValid || loading || !selectedProducts.length
                ? 'arrow-black-svg'
                : 'arrow-white-svg'
            "
            src="@/assets/icons/file/arrow_small_right.svg"
          />
        </v-btn>
      </v-col>
    </v-row>
  </v-form>

  <ProductQuestionModal ref="productQuestionModal" />
</template>

<script>
import {
  handleResponseError,
  shouldShowTooltip
} from '@/components/utils/globalFunction'
import ProductBreakdownService from '@/services/ProductBreakdownService'
import ProductQuestionModal from '@/components/modal/ProductQuestionModal.vue'
import ProductService from '@/services/ProductService'
import RepairFileService from '@/services/RepairFileService'
import { auth as $store } from '@/store/AuthModule'
import { RepairFile } from '@/models/RepairFile'
import Validator from '@/components/utils/validator'
import RepairFileStatusDetailConstant from '@/constants/repairFileStatusDetailConstant'
import ProductTypeConstant from '@/constants/productTypeConstant'

export default {
  name: 'StepBreakdownPage',
  components: { ProductQuestionModal },
  props: {
    currentStep: Number,
    currentRepairFile: {
      type: RepairFile,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      formStepBreakdownValid: false,
      description: null,
      selectedProductBreakdown: null,
      productBreakdownList: [],
      breakdownQuestionList: [],
      productList: [],
      selectedProducts: [],
      hoveredProductId: null,
      requiredRules: Validator.requiredRules
    }
  },
  async mounted() {
    await this.getProductBreakdownList()
  },
  computed: {
    productListWithoutPallet() {
      return this.productList.filter(
        (product) => product.product_type !== ProductTypeConstant.PALLET
      )
    }
  },
  methods: {
    shouldShowTooltip,
    async getProductBreakdownList() {
      try {
        const responseProductBreakdown = await ProductBreakdownService.getProductBreakdownList(
          this.currentRepairFile.product_boomerang_id
        )
        this.productBreakdownList = responseProductBreakdown.item_list
        await this.patchRepairFile()
      } catch (error) {
        handleResponseError(error)
      }
    },
    async patchRepairFile() {
      if (this.currentRepairFile.breakdown_description) {
        this.description = this.currentRepairFile.breakdown_description
      }
      if (this.currentRepairFile.breakdown_type_boomerang_id) {
        this.selectedProductBreakdown = this.productBreakdownList
          .filter((product) => {
            return product.boomerang_id === this.currentRepairFile.breakdown_type_boomerang_id
          })
          .map((product) => {
            return product.technical_id
          })
        if (this.selectedProductBreakdown) {
          this.currentRepairFile.breakdown_question_list.forEach((breakdown_question) => {
            this.breakdownQuestionList.push({
              breakdown_question_boomerang_id: breakdown_question.breakdown_question_boomerang_id,
              breakdown_question_answer: breakdown_question.breakdown_question_answer
            })
          })
          await this.getProductList()
        }
      } else if (this.currentRepairFile.product_guarantee_list.length) {
        await this.getProductList()
      }

      // Patch "élément atteint" value after all previous patch
      const productFound = this.productList.filter((item) => {
        return this.currentRepairFile.product_guarantee_list.some((guarantee) => {
          return item.boomerang_id === guarantee.product_boomerang_id
        })
      })

      this.selectedProducts = productFound
        .filter(function (product) {
          return product.reparable !== 'inactive'
        })
        .map(function (product) {
          return product.technical_id
        })
    },
    async openDialog() {
      const findProductBreakdown = this.productBreakdownList.find((productBreakdown) => {
        return productBreakdown.technical_id === parseInt(this.selectedProductBreakdown)
      })
      if (findProductBreakdown) {
        const responseModal = await this.$refs.productQuestionModal.show({
          productBreakdownId: findProductBreakdown.technical_id,
          productBreakdownDescription: findProductBreakdown.description,
          validateButton: this.$t('COMMON.BUTTON.CONTINUE')
        })
        if (responseModal) {
          this.breakdownQuestionList = responseModal
          await this.getProductList()
        }
      }
    },
    async getProductList() {
      try {
        const responseProduct = await ProductService.getProductList(
          this.currentRepairFile.product_boomerang_id
        )
        this.productList = responseProduct.item_list
        this.selectedProducts = []
        this.productList.forEach((product) => {
          if (this.currentRepairFile?.product_boomerang_id === product.boomerang_id) {
            this.selectedProducts.push(product.technical_id)
          }
        })
      } catch (error) {
        handleResponseError(error)
      }
    },
    checkProduct(product) {
      const foundIndex = this.selectedProducts.findIndex((selectedProduct) => {
        return selectedProduct === product.technical_id
      })
      if (foundIndex !== -1) {
        this.selectedProducts.splice(foundIndex, 1)
      } else {
        this.selectedProducts.push(product.technical_id)
      }
    },
    selectAll() {
      this.selectedProducts = this.productList
        .filter(function (product) {
          return product.reparable !== 'inactive'
        })
        .map(function (product) {
          return product.technical_id
        })
    },
    async resetSelectedProductBreakdown() {
      this.selectedProductBreakdown = null
      this.selectedProducts = []
      this.breakdownQuestionList = []
      this.productList = []
    },
    async submit() {
      const product_list_formatted = this.productList
        .filter((product) => {
          return this.selectedProducts.includes(product.technical_id)
        })
        .map((product) => {
          return product.boomerang_id
        })

      const formattedData = {
        status_detail: RepairFileStatusDetailConstant.BREAKDOWN_EVALUATED,
        is_complete_update: true,
        selected_products_boomerang_id: product_list_formatted,
        breakdown_description: this.description,
        requestor_collaborator_id: $store.state.currentCollaborator.technical_id
      }

      if (this.selectedProductBreakdown) {
        const findProductBreakdown = this.productBreakdownList.find((productBreakdown) => {
          return productBreakdown.technical_id === parseInt(this.selectedProductBreakdown)
        })

        formattedData['breakdown_type_boomerang_id'] = findProductBreakdown.boomerang_id
        formattedData['breakdown_question_list'] = this.breakdownQuestionList
      }

      try {
        const formData = new FormData()
        formData.append('data', JSON.stringify(formattedData))
        await RepairFileService.update(this.currentRepairFile.technical_id, formData)

        this.$emit('change-step', this.currentStep + 1)
      } catch (error) {
        handleResponseError(error)
      }
    },
    async updatePartialData() {
      const product_list_formatted = this.productList
        .filter((product) => {
          return this.selectedProducts.includes(product.technical_id)
        })
        .map((product) => {
          return product.boomerang_id
        })

      const formattedData = {
        status_detail: RepairFileStatusDetailConstant.BREAKDOWN_EVALUATED,
        selected_products_boomerang_id: product_list_formatted,
        breakdown_description: this.description,
        requestor_collaborator_id: $store.state.currentCollaborator.technical_id
      }

      if (this.selectedProductBreakdown) {
        const findProductBreakdown = this.productBreakdownList.find((productBreakdown) => {
          return productBreakdown.technical_id === parseInt(this.selectedProductBreakdown)
        })

        formattedData['breakdown_type_boomerang_id'] = findProductBreakdown.boomerang_id
        formattedData['breakdown_question_list'] = this.breakdownQuestionList
      }

      try {
        const formData = new FormData()
        formData.append('data', JSON.stringify(formattedData))
        await RepairFileService.update(this.currentRepairFile.technical_id, formData)
      } catch (error) {
        handleResponseError(error)
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  padding: 24px;
}

.v-form p {
  margin-bottom: 16px;
}

.show-sub-product {
  display: flex;
  padding: unset;
  color: #acacac;
  justify-content: end;
  text-decoration: underline;
}

.show-sub-product:hover {
  cursor: pointer;
}

.not-found-element {
  color: black;
  padding: 5px 25px;
  width: fit-content;
  text-align: center;
  border-radius: 10px;
  border: 1px solid black;
}

.not-found-element:hover {
  cursor: pointer;
}

.horizontal-scroll {
  overflow-x: auto;
  white-space: nowrap;
}

.product-wrapper {
  width: 250px;
}

.not-reparable .product-picture,
.not-reparable span,
.not-reparable p {
  opacity: 0.3;
}

.not-reparable:hover {
  cursor: not-allowed;
}

.product {
  display: flex;
  align-items: center;
  justify-items: center;
  flex-direction: column;
}

.product-picture {
  width: 50%;
  height: 150px;
  margin: 30px auto 40px;
}

.dialog {
  width: 320px;
  padding: 20px;
  background-color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.arrow-black-svg {
  height: 100%;
  width: 50%;
  filter: invert(0%) sepia(35%) saturate(13%) hue-rotate(154deg) brightness(96%) contrast(100%);
}

.arrow-white-svg {
  height: 100%;
  width: 50%;
  filter: invert(100%) sepia(0%) saturate(1%) hue-rotate(209deg) brightness(109%) contrast(101%);
}

.boom-button-transparent:hover .arrow-svg {
  filter: invert(48%) sepia(35%) saturate(7492%) hue-rotate(64deg) brightness(105%) contrast(79%);
}
</style>
